import { z } from "zod";

// ---------- GET /dashboard/api/usage/summary ----------

export const UsageSummaryItemSchema = z.object({
  key: z.union([z.string(), z.number()]).transform(String),
  count: z.number(),
  input_tokens: z.number(),
  output_tokens: z.number(),
  cost: z.string(),
});

export const UsageSummaryResponseSchema = z.object({
  tenant_id: z.string(),
  group_by: z.string(),
  buckets: z.array(UsageSummaryItemSchema),
});

// ---------- GET /dashboard/api/usage/savings ----------

export const UsageSavingsResponseSchema = z.object({
  tenant_id: z.string(),
  count: z.number(),
  cost_actual: z.string(),
  cost_requested: z.string(),
  cost_saved: z.string(),
  savings_pct: z.string(),
});

// ---------- GET /dashboard/api/routing/metrics ----------

export const RoutingMetricsResponseSchema = z.object({
  tenant_id: z.string(),
  total: z.number(),
  cache_hits: z.number(),
  cache_hit_rate: z.number(),
  errors: z.number(),
  blocks: z.number(),
  avg_latency_ms: z.number().nullable(),
  // Latency the Phantm pipeline adds before the upstream call (vs avg_latency_ms,
  // which is the upstream call itself). Optional so a brief backend/frontend
  // version skew during rollout doesn't fail the response parse.
  avg_pipeline_latency_ms: z.number().nullable().optional(),
});

// ---------- GET /dashboard/api/routing/policies ----------

export const RoutingPolicyItemSchema = z.object({
  policy_id: z.string(),
  name: z.string(),
  version: z.string(),
  status: z.string(),
  created_at: z.string().nullable(),
});

export const RoutingPoliciesResponseSchema = z.object({
  tenant_id: z.string(),
  policies: z.array(RoutingPolicyItemSchema),
});

// ---------- GET /dashboard/api/routing/policies/{policy_id} ----------

export const RoutingPolicyDetailResponseSchema = z.object({
  policy_id: z.string(),
  tenant_id: z.string(),
  name: z.string(),
  version: z.string(),
  status: z.string(),
  policy_json: z.record(z.unknown()),
  created_at: z.string().nullable(),
});

// ---------- POST /dashboard/api/routing/policies/{policy_id}/activate ----------

export const ActivatePolicyResponseSchema = z.object({
  status: z.string(),
  policy_id: z.string(),
  policy_status: z.string(),
});

// ---------- POST /dashboard/api/routing/dry-run ----------

export const DryRunResponseSchema = z.object({
  chosen_model: z.string(),
  reason_code: z.string().nullable(),
  rule_id: z.string().nullable(),
  features: z.record(z.unknown()).nullable(),
});

// ---------- GET /dashboard/api/tenant/budget ----------

export const BudgetResponseSchema = z.object({
  tenant_id: z.string(),
  monthly_budget_usd: z.union([z.string(), z.number()]).nullable(),
});

// ---------- POST /dashboard/api/tenant/budget ----------

export const SetBudgetResponseSchema = z.object({
  status: z.string(),
  tenant_id: z.string(),
  monthly_budget_usd: z.union([z.string(), z.number()]).nullable(),
});

// ---------- POST /dashboard/api/tenant/logging ----------

export const SetLoggingResponseSchema = z.object({
  status: z.string(),
  tenant_id: z.string(),
  content_logging_enabled: z.boolean(),
});

// ---------- GET /dashboard/api/requests ----------

export const RequestListItemSchema = z.object({
  id: z.string(),
  user_id: z.string().nullable(),
  feature: z.string().nullable(),
  env: z.string().nullable(),
  requested_model: z.string().nullable(),
  used_model: z.string().nullable(),
  action: z.string().nullable(),
  status: z.string().nullable(),
  latency_ms: z.number().nullable(),
  input_tokens_actual: z.number().nullable(),
  output_tokens_actual: z.number().nullable(),
  cost_actual: z.string().nullable(),
  cache_hit: z.boolean().nullable(),
  reason_code: z.string().nullable(),
  created_at: z.string().nullable(),
});

export const RequestListResponseSchema = z.object({
  tenant_id: z.string(),
  total: z.number(),
  offset: z.number(),
  limit: z.number(),
  items: z.array(RequestListItemSchema),
});

// ---------- GET /dashboard/api/requests/{request_id} ----------

export const RequestDetailRequestSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  user_id: z.string().nullable(),
  feature: z.string().nullable(),
  env: z.string().nullable(),
  requested_model: z.string().nullable(),
  used_model: z.string().nullable(),
  action: z.string().nullable(),
  status: z.string().nullable(),
  latency_ms: z.number().nullable(),
  input_tokens_est: z.number().nullable(),
  output_tokens_est: z.number().nullable(),
  cost_est: z.string().nullable(),
  input_tokens_actual: z.number().nullable(),
  output_tokens_actual: z.number().nullable(),
  cost_actual: z.string().nullable(),
  cache_hit: z.boolean().nullable(),
  cache_type: z.string().nullable(),
  created_at: z.string().nullable(),
});

export const RequestDetailRoutingSchema = z.object({
  policy_id: z.string().nullable(),
  rule_id: z.string().nullable(),
  reason_code: z.string().nullable(),
  chosen_model: z.string().nullable(),
  cache_hit: z.boolean(),
  status: z.string().nullable(),
  latency_ms: z.number().nullable(),
});

export const RequestDetailResponseSchema = z.object({
  request: RequestDetailRequestSchema,
  routing: RequestDetailRoutingSchema.optional(),
});

// ---------- GET /dashboard/api/users ----------

export const DashboardUserSchema = z.object({
  external_user_id: z.string(),
  status: z.string(),
  data_tier_override: z.string().nullable(),
  effective_data_tier: z.string(),
  monthly_budget_usd: z.string().nullable(),
});

export const UsersResponseSchema = z.object({
  tenant_id: z.string(),
  tenant_effective_tier: z.string(),
  // False on the Self-serve plan, where every End user is Collaborative.
  tier_editable: z.boolean().default(true),
  users: z.array(DashboardUserSchema),
});

export type DashboardUserPolicy = z.infer<typeof DashboardUserSchema>;

// ---------- POST /dashboard/api/users/{id}/tier|budget ----------

export const SetUserPolicyResponseSchema = z.object({
  status: z.string(),
  external_user_id: z.string(),
  data_tier_override: z.string().nullable(),
  effective_data_tier: z.string(),
  monthly_budget_usd: z.string().nullable(),
});

// ---------- GET /dashboard/api/prompt-stability ----------

export const PromptStabilityResponseSchema = z.object({
  tenant_id: z.string(),
  enabled: z.boolean(),
  families: z.array(z.record(z.unknown())),
});
